import { FC } from 'react';
import { cn } from '@/shared/lib';
import { Tooltip } from '@/shared/ui';
import type { ConversationAttachmentResponse, PhraseAttachmentInPhraseResponse } from '../../model/api/types';

/**
 * Пропсы внутреннего компонента-ссылки {@link AttachmentLink}.
 */
type Props = {
  /** Вложение, чью ссылку отрисовываем. */
  attachment: ConversationAttachmentResponse | PhraseAttachmentInPhraseResponse;
  /** Подпись вложения. */
  name: string;
  /** Файл получить нельзя — подпись не открывает его. */
  isUnavailable: boolean;
  /** Идёт получение адреса файла — заблокировать кнопку. */
  isLoading: boolean;
  /** Открыть файл, адрес которого нужно сначала получить. */
  onOpen: () => void;
  /** Компактный режим — наследуем цвет текста родителя (для облака чата). */
  compact: boolean;
};

/**
 * Ссылка/кнопка открытия вложения.
 *
 * Для внешних ссылок — `<a target="_blank">`, для файлов хранилища — `<button>`, получающий адрес
 * файла; недоступное вложение — подпись без действия.
 */
export const AttachmentLink: FC<Props> = ({ attachment, name, isUnavailable, isLoading, onOpen, compact }) => {
  const labelClassName = 'block w-full truncate text-sm';
  // В облаке транскрипта фон — primary/secondary; используем currentColor родителя,
  // в табе вложений — стандартный акцентный цвет ссылки
  const linkColorClass = compact ? 'text-current' : 'text-primary';

  if (isUnavailable) {
    return <span className={cn(labelClassName, 'text-muted-foreground')}>{name}</span>;
  }

  // Подпись ссылки укорочена, поэтому полный адрес показывается подсказкой — общей для интерфейса,
  // а не встроенной браузерной: та выглядит иначе и появляется по своим правилам.
  if (attachment.isExternalUrl && attachment.url) {
    return (
      <Tooltip tooltip={attachment.url}>
        <a
          href={attachment.url}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(labelClassName, linkColorClass, 'hover:underline')}
        >
          {name}
        </a>
      </Tooltip>
    );
  }

  // У файла локального хранилища ссылки нет — подсказкой служит полное имя
  return (
    <Tooltip tooltip={attachment.url || name}>
      <button
        type="button"
        onClick={onOpen}
        disabled={isLoading}
        className={cn(
          labelClassName,
          linkColorClass,
          'text-left hover:underline disabled:cursor-progress disabled:opacity-60',
        )}
      >
        {name}
      </button>
    </Tooltip>
  );
};
